import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Table, Switch, Input, Space, message, Row, Col, Typography, Tooltip, Divider, Modal } from 'antd';
import { SettingOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import menuConfigService from '../../../services/menuConfigService';

const { Title, Text } = Typography;

// 6个扁平角色键
type RoleKey = 'admin' | 'hq_staff' | 'warehouse_supervisor' | 'warehouse_staff' | 'customer_main_account' | 'customer_sub_account';

const ROLE_DEFS: Array<{ key: RoleKey; label: string }> = [
  { key: 'admin', label: '管理员' },
  { key: 'hq_staff', label: '总部员工' },
  { key: 'warehouse_supervisor', label: '仓库主管' },
  { key: 'warehouse_staff', label: '仓库员工' },
  { key: 'customer_main_account', label: '客户主账号' },
  { key: 'customer_sub_account', label: '客户子账号' },
];

interface MenuRow {
  id: number;
  name: string;
  name_en?: string;
  path: string;
  parent?: number | null;
  sort_order: number;
}

interface RoleState { is_visible: boolean; custom_path: string }

const RoleConfigManagement: React.FC = () => {
  const { t } = useTranslation();
  const [allMenus, setAllMenus] = useState<MenuRow[]>([]);
  const [roleStates, setRoleStates] = useState<Record<RoleKey, Map<number, RoleState>>>(() => ({
    admin: new Map(),
    hq_staff: new Map(),
    warehouse_supervisor: new Map(),
    warehouse_staff: new Map(),
    customer_main_account: new Map(),
    customer_sub_account: new Map(),
  }));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const tableMinX = useMemo(() => 180 + 180 + ROLE_DEFS.length * 180, []);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleKey | null>(null);
  const [editingMenu, setEditingMenu] = useState<MenuRow | null>(null);
  const [editingValue, setEditingValue] = useState('');
  // 调试 refs/state
  const rootRef = useRef<HTMLDivElement | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const [debugVisible, setDebugVisible] = useState(false);
  const [metrics, setMetrics] = useState<any>(null);

  // 菜单辅助索引：id -> menu，parent -> childrenIds
  const menuById = useMemo(() => {
    const map = new Map<number, MenuRow>();
    allMenus.forEach(m => map.set(m.id, m));
    return map;
  }, [allMenus]);

  const childrenMap = useMemo(() => {
    const map = new Map<number, number[]>();
    allMenus.forEach(m => map.set(m.id, []));
    allMenus.forEach(m => {
      const pid = m.parent || null;
      if (pid && map.has(pid)) {
        map.get(pid)!.push(m.id);
      }
    });
    return map;
  }, [allMenus]);

  // 加载菜单 + 六个角色配置
  const loadAll = async () => {
    try {
      setLoading(true);
      const menus = (await menuConfigService.getMenuItems()) as any[];
      const normalized: MenuRow[] = menus
        .map(m => ({ id: m.id, name: m.name, name_en: m.name_en, path: m.path || '', parent: m.parent, sort_order: m.sort_order }))
        .sort((a, b) => {
          const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
          return so !== 0 ? so : a.id - b.id;
        });
      setAllMenus(normalized);

      const results = await Promise.all(ROLE_DEFS.map(r => menuConfigService.getRoleConfigs(r.key)));
      const nextStates: Record<RoleKey, Map<number, RoleState>> = {
        admin: new Map(),
        hq_staff: new Map(),
        warehouse_supervisor: new Map(),
        warehouse_staff: new Map(),
        customer_main_account: new Map(),
        customer_sub_account: new Map(),
      };
      results.forEach((cfgs: any[], idx) => {
        const role = ROLE_DEFS[idx].key;
        (cfgs || []).forEach(c => {
          nextStates[role].set(c.menu, { is_visible: !!c.is_visible, custom_path: c.custom_path || '' });
        });
      });
      setRoleStates(nextStates);
    } catch (e) {
      console.error('加载角色菜单配置失败:', e);
      message.error('加载角色菜单配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRoleValue = (role: RoleKey, menuId: number, updater: (prev: RoleState) => RoleState) => {
    setRoleStates(prev => {
      const copy: Record<RoleKey, Map<number, RoleState>> = { ...prev } as any;
      const map = new Map(copy[role]);
      const current = map.get(menuId) || { is_visible: false, custom_path: '' };
      map.set(menuId, updater(current));
      copy[role] = map;
      return copy;
    });
  };

  // 父菜单开关时，级联影响其所有子孙菜单
  const setRoleVisibleCascade = (role: RoleKey, menuId: number, visible: boolean) => {
    setRoleStates(prev => {
      const copy: Record<RoleKey, Map<number, RoleState>> = { ...prev } as any;
      const map = new Map(copy[role]);

      const applyTo = (id: number) => {
        const menu = menuById.get(id);
        const current = map.get(id) || { is_visible: false, custom_path: '' };
        map.set(id, {
          is_visible: visible,
          custom_path: visible ? (current.custom_path || menu?.path || '') : ''
        });
      };

      // 自身
      applyTo(menuId);
      // 所有子孙
      const stack = [...(childrenMap.get(menuId) || [])];
      while (stack.length > 0) {
        const cid = stack.pop() as number;
        applyTo(cid);
        const kids = childrenMap.get(cid) || [];
        if (kids.length > 0) stack.push(...kids);
      }

      copy[role] = map;
      return copy;
    });
  };

  const selectAllForRole = (role: RoleKey) => {
    setRoleStates(prev => {
      const copy: Record<RoleKey, Map<number, RoleState>> = { ...prev } as any;
      const newMap = new Map(copy[role]);
      allMenus.forEach(m => {
        const current = newMap.get(m.id) || { is_visible: false, custom_path: '' };
        newMap.set(m.id, { is_visible: true, custom_path: current.custom_path || m.path || '' });
      });
      copy[role] = newMap;
      return copy;
    });
  };

  const unselectAllForRole = (role: RoleKey) => {
    setRoleStates(prev => {
      const copy: Record<RoleKey, Map<number, RoleState>> = { ...prev } as any;
      const newMap = new Map(copy[role]);
      allMenus.forEach(m => {
        newMap.set(m.id, { is_visible: false, custom_path: '' });
      });
      copy[role] = newMap;
      return copy;
    });
  };

  const openEdit = (role: RoleKey, record: MenuRow) => {
    const st = roleStates[role].get(record.id) || { is_visible: false, custom_path: '' };
    setEditingRole(role);
    setEditingMenu(record);
    setEditingValue(st.custom_path || record.path || '');
    setEditModalVisible(true);
  };

  const handleEditOk = () => {
    if (editingRole && editingMenu) {
      setRoleValue(editingRole, editingMenu.id, prev => ({ ...prev, custom_path: editingValue }));
    }
    setEditModalVisible(false);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      for (const { key } of ROLE_DEFS) {
        const map = roleStates[key];
        const payload = allMenus.map(m => {
          const st = map.get(m.id);
          return {
            menu_id: m.id,
            is_visible: !!st?.is_visible,
            custom_path: st?.custom_path || '',
          };
        });
        await menuConfigService.batchUpdateRoleConfigs(key, '', payload as any);
      }
      message.success('已保存全部角色的菜单配置');
    } catch (e) {
      console.error('保存失败:', e);
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: '菜单名称',
        dataIndex: 'name',
        key: 'name',
        width: 180,
        ellipsis: true,
      },
      {
        title: '默认路径',
        dataIndex: 'path',
        key: 'path',
        width: 180,
        ellipsis: true,
        render: (v: string) => <code>{v}</code>,
      },
    ];
    ROLE_DEFS.forEach(({ key, label }) => {
      const totalCount = allMenus.length;
      const selectedCount = allMenus.reduce((acc, m) => acc + (roleStates[key].get(m.id)?.is_visible ? 1 : 0), 0);
      cols.push({
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              {label}
              <Button size="small" type="link" onClick={() => selectAllForRole(key)} disabled={selectedCount >= totalCount} style={{ paddingLeft: 6 }}>全选</Button>
              <Button size="small" type="link" onClick={() => unselectAllForRole(key)} disabled={selectedCount === 0} style={{ paddingLeft: 6 }}>取消全选</Button>
            </span>
            <span style={{ color: '#999' }}>{selectedCount}/{totalCount}</span>
          </div>
        ),
        key,
        width: 180,
        render: (_: any, record: MenuRow) => {
          const st = roleStates[key].get(record.id) || { is_visible: false, custom_path: '' };
          return (
            <Space size={6}>
              <Switch
                checked={st.is_visible}
                onChange={(checked) => setRoleVisibleCascade(key, record.id, checked)}
                checkedChildren="可见"
                unCheckedChildren="隐藏"
              />
              <Button size="small" disabled={!st.is_visible} onClick={() => openEdit(key, record)}>设置路径</Button>
            </Space>
          );
        }
      });
    });
    return cols;
  }, [roleStates, allMenus]);

  // 调试：采集宽度信息
  const collectMetrics = () => {
    try {
      const viewportW = window.innerWidth;
      const docW = document.documentElement?.scrollWidth || 0;
      const bodyW = document.body?.scrollWidth || 0;
      const menuEl = document.querySelector('.menu-management') as HTMLElement | null;
      const roleEl = rootRef.current;
      const wrapEl = tableWrapperRef.current;
      const tableEl = wrapEl ? (wrapEl.querySelector('.ant-table') as HTMLElement | null) : null;
      const tabsEl = document.querySelector('.ant-tabs') as HTMLElement | null;
      const tabsHolderEl = document.querySelector('.ant-tabs-content-holder') as HTMLElement | null;
      const layoutEl = document.querySelector('.ant-layout') as HTMLElement | null;
      const layoutContentEl = document.querySelector('.ant-layout-content') as HTMLElement | null;
      const rootEl = document.getElementById('root') as HTMLElement | null;

      const val: any = {
        viewportW,
        documentScrollW: docW,
        bodyScrollW: bodyW,
        menuManagement: menuEl ? { clientW: menuEl.clientWidth, scrollW: menuEl.scrollWidth } : null,
        roleConfig: roleEl ? { clientW: roleEl.clientWidth, scrollW: roleEl.scrollWidth } : null,
        tableWrapper: wrapEl ? { clientW: wrapEl.clientWidth, scrollW: wrapEl.scrollWidth } : null,
        table: tableEl ? { clientW: tableEl.clientWidth, scrollW: tableEl.scrollWidth } : null,
        antTabs: tabsEl ? { clientW: tabsEl.clientWidth, scrollW: tabsEl.scrollWidth } : null,
        antTabsContentHolder: tabsHolderEl ? { clientW: tabsHolderEl.clientWidth, scrollW: tabsHolderEl.scrollWidth } : null,
        antLayout: layoutEl ? { clientW: layoutEl.clientWidth, scrollW: layoutEl.scrollWidth } : null,
        antLayoutContent: layoutContentEl ? { clientW: layoutContentEl.clientWidth, scrollW: layoutContentEl.scrollWidth } : null,
        root: rootEl ? { clientW: rootEl.clientWidth, scrollW: rootEl.scrollWidth } : null,
      };

      // 扫描全页找出 scrollWidth 最大的前若干元素
      const scanOffenders = () => {
        const nodes = Array.from(document.querySelectorAll<HTMLElement>('body *'));
        const items = nodes.map((el) => ({
          el,
          scrollW: el.scrollWidth || 0,
          clientW: el.clientWidth || 0,
        }));
        items.sort((a, b) => b.scrollW - a.scrollW);
        const top = items.slice(0, 5).map((x) => ({
          tag: x.el.tagName.toLowerCase(),
          class: x.el.className,
          id: x.el.id,
          scrollW: x.scrollW,
          clientW: x.clientW,
        }));
        return top;
      };

      const offenders: Array<{ name: string; width: number }> = [];
      offenders.push({ name: 'document', width: docW });
      if (menuEl) offenders.push({ name: 'menuManagement', width: menuEl.scrollWidth });
      if (roleEl) offenders.push({ name: 'roleConfig', width: roleEl.scrollWidth });
      if (wrapEl) offenders.push({ name: 'tableWrapper', width: wrapEl.scrollWidth });
      if (tableEl) offenders.push({ name: 'table', width: tableEl.scrollWidth });
      if (tabsEl) offenders.push({ name: 'antTabs', width: tabsEl.scrollWidth });
      if (tabsHolderEl) offenders.push({ name: 'antTabsContentHolder', width: tabsHolderEl.scrollWidth });
      if (layoutEl) offenders.push({ name: 'antLayout', width: layoutEl.scrollWidth });
      if (layoutContentEl) offenders.push({ name: 'antLayoutContent', width: layoutContentEl.scrollWidth });
      if (rootEl) offenders.push({ name: 'root', width: rootEl.scrollWidth });
      const maxOffender = offenders.reduce((acc, cur) => (cur.width > acc.width ? cur : acc), { name: 'viewport', width: viewportW });
      val.maxOffender = maxOffender;
      val.hasPageOverflow = docW > viewportW;
      val.columnsBase = { nameCol: 180, pathCol: 180, roleCol: 180, roles: ROLE_DEFS.length, minX: tableMinX };
      val.topDomOffenders = scanOffenders();

      setMetrics(val);
      // eslint-disable-next-line no-console
      console.log('[RoleConfig Width Metrics]', val);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('collectMetrics error', e);
    }
  };

  useEffect(() => {
    if (!debugVisible) return;
    collectMetrics();
    const onResize = () => collectMetrics();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debugVisible]);

  // 构建树形数据（仅用于前端展示，不改变保存逻辑）
  const buildTreeData = (menus: MenuRow[]): any[] => {
    const map = new Map<number, any>();
    const roots: any[] = [];
    menus.forEach(m => map.set(m.id, { ...m, children: [] }));
    menus.forEach(m => {
      const node = map.get(m.id)!;
      const pid = m.parent || null;
      if (pid && map.has(pid)) {
        map.get(pid)!.children.push(node);
      } else {
        roots.push(node);
      }
    });
    const sortNodes = (nodes: any[]) => {
      nodes.sort((a, b) => {
        const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        return so !== 0 ? so : a.id - b.id;
      });
      nodes.forEach(n => Array.isArray(n.children) && n.children.length > 0 && sortNodes(n.children));
    };
    sortNodes(roots);
    return roots;
  };

  const filterTree = (nodes: any[], q: string): any[] => {
    if (!q) return nodes;
    const query = q.toLowerCase();
    const match = (n: MenuRow) =>
      (n.name || '').toLowerCase().includes(query) ||
      (n.name_en || '').toLowerCase().includes(query) ||
      (n.path || '').toLowerCase().includes(query);
    const dfs = (list: any[]): any[] => {
      const res: any[] = [];
      list.forEach(n => {
        const children = Array.isArray(n.children) ? dfs(n.children) : [];
        if (match(n) || children.length > 0) res.push({ ...n, children });
      });
      return res;
    };
    return dfs(nodes);
  };

  const treeData = useMemo(() => filterTree(buildTreeData(allMenus), searchQuery), [allMenus, searchQuery]);

  const computeExpandedKeys = (nodes: any[]): React.Key[] => {
    const keys: React.Key[] = [];
    const dfs = (list: any[]) => {
      list.forEach(n => {
        if (Array.isArray(n.children) && n.children.length > 0) {
          keys.push(n.id);
          dfs(n.children);
        }
      });
    };
    dfs(nodes);
    return keys;
  };

  return (
    <div ref={rootRef} className="role-config-management" style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', rowGap: 8 }}>
          <div>
            <Title level={4}><SettingOutlined style={{ marginRight: 8 }} /> 角色菜单配置</Title>
          </div>
          <Space>
            <Input.Search
              placeholder="搜索菜单（名称/英文名/路径）"
              allowClear
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 320 }}
            />
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveAll}>保存全部</Button>
            <Tooltip title="重新加载菜单与六个角色配置"><Button icon={<ReloadOutlined />} loading={loading} onClick={loadAll}>刷新</Button></Tooltip>
          </Space>
        </div>

        <Divider />
        <div ref={tableWrapperRef} style={{ width: '100%', overflowX: 'auto' }}>
          <Table
            style={{ minWidth: tableMinX }}
            columns={columns as any}
            dataSource={treeData as any}
            rowKey="id"
            loading={loading}
            pagination={false}
            tableLayout="fixed"
            expandable={{
              expandedRowKeys: computeExpandedKeys(treeData as any),
              onExpandedRowsChange: () => {},
              expandIcon: () => <span style={{ display: 'inline-block', width: 16 }} />,
              indentSize: 16,
            }}
            scroll={{ y: 520 }}
            size="small"
          />
        </div>
      </Card>
      {debugVisible && (
        <div style={{ position: 'fixed', right: 12, bottom: 12, maxWidth: 420, background: 'rgba(0,0,0,0.75)', color: '#fff', padding: 12, borderRadius: 8, zIndex: 9999, fontSize: 12, lineHeight: 1.4 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>宽度调试</div>
          <div>viewport: {metrics?.viewportW}</div>
          <div>document.scrollWidth: {metrics?.documentScrollW}</div>
          <div>body.scrollWidth: {metrics?.bodyScrollW}</div>
          <div>menuManagement: {metrics?.menuManagement ? `${metrics.menuManagement.clientW}/${metrics.menuManagement.scrollW}` : 'N/A'}</div>
          <div>roleConfig: {metrics?.roleConfig ? `${metrics.roleConfig.clientW}/${metrics.roleConfig.scrollW}` : 'N/A'}</div>
          <div>wrapper: {metrics?.tableWrapper ? `${metrics.tableWrapper.clientW}/${metrics.tableWrapper.scrollW}` : 'N/A'}</div>
          <div>table: {metrics?.table ? `${metrics.table.clientW}/${metrics.table.scrollW}` : 'N/A'}</div>
          <div>hasPageOverflow: {String(metrics?.hasPageOverflow)}</div>
          <div>maxOffender: {metrics?.maxOffender ? `${metrics.maxOffender.name} = ${metrics.maxOffender.width}` : 'N/A'}</div>
          <div>minX(base): 180 + 180 + roles {ROLE_DEFS.length} × 180 = {tableMinX}</div>
          <div style={{ marginTop: 8 }}>
            <Button size="small" onClick={collectMetrics}>重新采集</Button>
          </div>
        </div>
      )}
      <Modal
        title={editingRole && editingMenu ? `设置路径 - ${editingMenu.name}（${(ROLE_DEFS.find(r => r.key === editingRole)?.label) || editingRole}）` : '设置路径'}
        open={editModalVisible}
        onOk={handleEditOk}
        onCancel={() => setEditModalVisible(false)}
        destroyOnClose
      >
        <Input
          placeholder={editingMenu?.path || '/'}
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default RoleConfigManagement;